/**
 * Security Utilities — تاجر برو
 *
 * Covers:
 * • Input sanitisation (XSS prevention)
 * • File-upload validation (type + size)
 * • Email / phone validation helpers
 * • CSRF token management (sessionStorage)
 * • Security headers helper for fetch()
 * • HTML content sanitisation for display
 */

// ─── Input Sanitisation ──────────────────────────────────────────────────
const HTML_ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

/**
 * Escape HTML special characters to prevent XSS.
 * @param {unknown} input
 * @returns {string}
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return String(input ?? '');
  return input.replace(/[&<>"'`=/]/g, (ch) => HTML_ESCAPE_MAP[ch]);
};

/**
 * Recursively sanitize all string values in an object / array.
 * Safe to call before displaying user-supplied content.
 * @param {unknown} obj
 * @returns {unknown}
 */
export const sanitizeObject = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return sanitizeInput(obj);
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, sanitizeObject(v)]),
  );
};

// ─── File Upload Validation ───────────────────────────────────────────────
/**
 * Validate a File object before uploading.
 *
 * @param {File} file
 * @param {object} [options]
 * @param {number}   [options.maxSize=10MB]           – bytes
 * @param {string[]} [options.allowedTypes]            – MIME types
 * @param {string[]} [options.allowedExtensions]       – e.g. ['.pdf']
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize          = 10 * 1024 * 1024,
    allowedTypes     = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
  } = options;

  if (!file) return { valid: false, error: 'لم يتم اختيار ملف' };

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `حجم الملف يتجاوز الحد الأقصى (${(maxSize / (1024 * 1024)).toFixed(1)} ميجابايت)`,
    };
  }

  if (allowedTypes.length && !allowedTypes.includes(file.type)) {
    return { valid: false, error: 'نوع الملف غير مسموح به' };
  }

  const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (allowedExtensions.length && !allowedExtensions.includes(ext)) {
    return { valid: false, error: 'امتداد الملف غير مسموح به' };
  }

  // Double-check: MIME type must match extension (prevents MIME confusion)
  const mimeExtMap = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png':  ['.png'],
    'image/gif':  ['.gif'],
    'application/pdf': ['.pdf'],
  };
  const expectedExts = mimeExtMap[file.type];
  if (expectedExts && !expectedExts.includes(ext)) {
    return { valid: false, error: 'نوع الملف لا يتطابق مع الامتداد' };
  }

  return { valid: true, error: null };
};

// ─── Validation Helpers ───────────────────────────────────────────────────
/** Validate email format */
export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Validate Saudi mobile number (05XXXXXXXX or +9665XXXXXXXX) */
export const isValidSaudiPhone = (phone) =>
  /^(\+966|0)?5\d{8}$/.test(phone.replace(/\s+/g, ''));

// ─── CSRF Token Management ────────────────────────────────────────────────
/**
 * CSRF token stored in sessionStorage (cleared on tab close).
 * Injected into every API request by apiClient (lib/api.js).
 */
export const csrfToken = {
  get: ()       => sessionStorage.getItem('csrfToken') ?? '',
  set: (t)      => sessionStorage.setItem('csrfToken', t),
  generate: ()  => {
    const arr   = new Uint8Array(32);
    crypto.getRandomValues(arr);
    const token = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem('csrfToken', token);
    return token;
  },
  validate: (t) => t === sessionStorage.getItem('csrfToken'),
};

// ─── Security Headers for fetch() ────────────────────────────────────────
/**
 * Merge security-related headers into an existing headers object.
 * Use when calling fetch() directly (prefer apiClient which handles this automatically).
 * @param {Record<string, string>} [headers]
 * @returns {Record<string, string>}
 */
export const addSecurityHeaders = (headers = {}) => ({
  ...headers,
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options':        'DENY',
  'X-CSRF-Token':           csrfToken.get(),
});

// ─── Safe URL check ───────────────────────────────────────────────────────
/**
 * Verify a URL is same-origin or matches REACT_APP_BACKEND_URL.
 * Prevents open-redirect attacks.
 * @param {string} url
 * @returns {boolean}
 */
export const isSafeUrl = (url) => {
  try {
    const parsed   = new URL(url, window.location.origin);
    const backendOrigin = process.env.REACT_APP_BACKEND_URL
      ? new URL(process.env.REACT_APP_BACKEND_URL).origin
      : null;
    return (
      parsed.origin === window.location.origin ||
      (backendOrigin && parsed.origin === backendOrigin)
    );
  } catch {
    return false;
  }
};

export default {
  sanitizeInput,
  sanitizeObject,
  validateFileUpload,
  isValidEmail,
  isValidSaudiPhone,
  csrfToken,
  addSecurityHeaders,
  isSafeUrl,
};
